import { playerService } from './playerService';
import { coachService } from './coachService';
import { officialService } from './officialService';
import { teamService } from './teamService';
import { regionalTeamRosterService } from './regionalTeamRosterService';

const entityCache = {};

const buildLabel = (name, licenseNumber, subtitle) => {
  const parts = [name || 'Unnamed'];
  if (licenseNumber) parts.push(licenseNumber);
  if (subtitle) parts.push(subtitle);
  return parts.filter(Boolean).join(' · ');
};

const toOption = ({ id, name, licenseNumber = '', region = '', subtitle = '' }) => ({
  value: id,
  label: buildLabel(name, licenseNumber, subtitle),
  entity: { id, name, licenseNumber, region },
});

const sortOptions = (options) =>
  [...options].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

export const financeEntityService = {
  clearCache() {
    Object.keys(entityCache).forEach((key) => delete entityCache[key]);
  },

  async getOptions(entityType) {
    if (!entityType || entityType === 'other') return [];
    if (entityCache[entityType]) return entityCache[entityType];

    let options = [];

    switch (entityType) {
      case 'player': {
        const players = await playerService.getAll();
        options = players.map((player) =>
          toOption({
            id: player.id,
            name: player.userProfile?.fullName || 'Unknown Player',
            licenseNumber: player.licenseNumber,
            region: player.team?.region,
            subtitle: player.team?.name,
          })
        );
        break;
      }
      case 'coach': {
        const coaches = await coachService.getAll();
        options = coaches.map((coach) =>
          toOption({
            id: coach.id,
            name: coach.coachName,
            licenseNumber: coach.licenseNumber,
            region: coach.region,
            subtitle: coach.grade,
          })
        );
        break;
      }
      case 'official': {
        const officials = await officialService.getAll();
        options = officials.map((official) =>
          toOption({
            id: official.id,
            name: official.userProfile?.fullName || 'Unknown Official',
            licenseNumber: official.licenseNumber,
            subtitle: official.certificationLevel,
          })
        );
        break;
      }
      case 'regional_player': {
        const players = await regionalTeamRosterService.getAllPlayers();
        options = players.map((player) =>
          toOption({
            id: player.id,
            name: player.fullName,
            region: player.teamRegion,
            subtitle: player.teamName,
          })
        );
        break;
      }
      case 'club': {
        const teams = await teamService.getAll();
        options = teams.map((team) =>
          toOption({
            id: team.id,
            name: team.name,
            region: team.region,
            subtitle: team.category,
          })
        );
        break;
      }
      default:
        options = [];
    }

    entityCache[entityType] = sortOptions(options);
    return entityCache[entityType];
  },

  /** Keep saved entity visible when editing older records. */
  withSavedEntity(options = [], { entityId, entityName, licenseNumber, region } = {}) {
    if (!entityId && !entityName?.trim()) return options;

    const exists = options.some(
      (option) =>
        (entityId && option.value === entityId) ||
        (entityName && option.entity?.name === entityName.trim())
    );

    if (exists) return options;

    const savedOption = toOption({
      id: entityId || entityName.trim(),
      name: entityName.trim() || 'Saved entity',
      licenseNumber,
      region,
    });

    return [savedOption, ...options];
  },
};
